import { CSSProperties, useEffect, useRef, useState } from "react";
// import Image from "next/image";
import { composeClassNames, withDefault } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import {
  Alert,
  Col,
  Image,
  OverlayTrigger,
  Popover,
  PopoverContent,
  Row,
} from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import Heading from "src/components/Heading";
import Hr from "src/components/Hr";
import IntroductionActions from "src/components/IntroductionActions";
import IntroductionInfo from "src/components/IntroductionInfo";
import Mux from "src/components/Mux";
import Price from "src/components/Price";
import PriceVariants from "src/components/PriceVariants";
import PublishButtons from "src/components/PublishButtons";
import ReadMore from "src/components/ReadMore";
import RichText from "src/components/RichText";
import SellButtons from "src/components/SellButtons";
import Text from "src/components/Text";
import Translate from "src/components/Translate";
import VideoPlayer from "src/components/VideoPlayer";
import constants from "src/constants";
import getLink from "src/functions/getLink";
import gutter from "src/functions/gutter";
import profileNameText from "src/functions/profileNameText";
import translate from "src/functions/translate";
import Play from "src/icons/Play";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sIntroduction } from "src/styles/sIntroduction.module.scss";
import { sMuxThumbnail } from "src/styles/sMuxThumbnail.module.scss";
import { sPopover } from "src/styles/sPopover.module.scss";
import { sSerieIntroduction } from "src/styles/sSerieIntroduction.module.scss";
import { ApiPrograms } from "src/types/ApiPrograms";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiPrograms;
  freshDataLoaded: boolean;
};

function SerieIntroduction({ style, className, data, freshDataLoaded }: Props) {
  const { locale: lang } = useRouter();
  const [newPrice, setNewPrice] = useState(data?.price);
  const [priceVariants, setPriceVariants] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const titleRef = useRef(null);

  useEffect(() => {
    try {
      const pv = require(`src/customs/serie_${data.id}_PriceVariants`);
      setPriceVariants({ prices: pv.prices, label: pv.pricesLabel });
    } catch (err) {}
    try {
      const deadlineComponent = require(`src/customs/serie_${data.id}_Deadline`);
      setDeadline(deadlineComponent.Deadline);
    } catch (err) {}
  }, [data.id]);

  const popover = (
    <Popover
      id="popover-alert"
      className={composeClassNames([sPopover, "popover-warning"])}
    >
      <PopoverContent>
        <Text width={`semibold`} size={`xn`}>
          <Translate tKey={`You need to purchase a serie to play the video.`} />
        </Text>
      </PopoverContent>
    </Popover>
  );

  const profileDefined =
    data.profiles && Object.keys(data.profiles).length > 0 ? true : false;
  const videoDefined =
    data.video &&
    Object.keys(data.video).length > 0 &&
    data.video.video_id?.length > 0
      ? true
      : false;
  const videoMuxPaid =
    constants.Show.contentMux &&
    data.video &&
    Object.keys(data.video).length > 0 &&
    data.video.mux &&
    Object.keys(data.video.mux).length > 0 &&
    data.video.mux.playback_id?.length > 0 &&
    data.isPaid
      ? true
      : false;
  const serieExpired = data && data.end_at && new Date(data.end_at);
  const actualDate = new Date();
  const isSerieExpired = serieExpired ? actualDate > serieExpired : false;

  return data ? (
    <Row
      style={style}
      className={composeClassNames([
        sIntroduction,
        sSerieIntroduction,
        className,
        "serie-introduction",
        videoMuxPaid && "full-width",
      ])}
      ref={titleRef}
    >
      <Col lg={videoDefined ? 7 : 4} className={"serie-preview"}>
        {videoMuxPaid && (
          <Mux videoSource={data.video?.mux?.playback_id} fullWidth={true} />
        )}
        {!videoMuxPaid && videoDefined && (
          <VideoPlayer
            data={data.video}
            thumbnail={data.thumbnail}
            fullWidth={false}
          />
        )}
        {!videoMuxPaid && !videoDefined && (
          <div className={composeClassNames([sMuxThumbnail, "mux-thumbnail"])}>
            <Image
              //width={16}
              //height={9}
              //layout="responsive"
              className="mux-thumbnail-image"
              src={data?.thumbnail?.url}
              alt={data.title}
            />
            <OverlayTrigger trigger="click" placement="top" overlay={popover}>
              <Play className="mux-thumbnail-play-button" />
            </OverlayTrigger>
          </div>
        )}
        <PublishButtons className={`serie-buttons`} />
      </Col>
      <Col lg={videoDefined ? 5 : 8} className={"serie-detail"}>
        <Heading type={`h1`} title={data.title} />
        {profileDefined && (
          <IntroductionInfo
            type={`serie`}
            data={[
              {
                item: profileNameText(
                  data.profiles[0].client?.name,
                  data.profiles[0].client?.surname,
                  data.profiles[0].nickname,
                  data.profiles[0].type
                ),
                url: getLink(
                  data.profiles[0].locale,
                  `profile`,
                  data.profiles[0].slug
                ),
              },
              { item: data.items },
              { item: String(Math.round(data.views / 100)) },
            ]}
          />
        )}
        <IntroductionActions
          data={{
            favorite: true,
            rating: withDefault(data.rating, 0),
            paid: data.paid,
            isPaid: data.isPaid,
            subscription: data.subscription,
          }}
        />
        {freshDataLoaded && data.price && (
          <>
            {data.isPaid && (
              <Alert
                variant={`info`}
                className={composeClassNames([sAlert])}
                style={gutter(`margin`, `top`, `lg`)}
              >
                <Translate tKey={`You have purchased the serie.`} />
              </Alert>
            )}
            {!data.isPaid &&
              !isSerieExpired &&
              data.price &&
              (!priceVariants ? (
                <Price
                  currency={`CZK`} // TODO: currency should be dynamic AND implement price basic
                  price={data.price}
                  priceBasic={data.price_basic}
                  size={`lg`}
                />
              ) : (
                <PriceVariants
                  currency={`CZK`} // TODO: currency should be dynamic AND implement price basic
                  price={data.price}
                  priceAlternatives={priceVariants.prices}
                  /* @ts-ignore */
                  setNewPrice={setNewPrice}
                  size={`xm`}
                  title={translate(
                    priceVariants.label || `Optional contribution`,
                    lang
                  )}
                />
              ))}
            {!data.isPaid && constants.Show.sale && !isSerieExpired && (
              <>
                <SellButtons
                  entityId={data.id}
                  className={`align-left`}
                  id={1}
                  inCart={data.inCart}
                  newPrice={newPrice ? newPrice : null}
                  orderId={""}
                  type={"program"} // TODO: there is "program", but should be "serie" instead
                />
                {data.inCart && (
                  <Alert
                    variant={`info`}
                    className={composeClassNames([sAlert])}
                    style={gutter(`margin`, `top`, `lg`)}
                  >
                    <Translate tKey={`Serie is already in the cart.`} />
                  </Alert>
                )}
              </>
            )}
            {deadline && deadline}
          </>
        )}
        {isSerieExpired && (
          <Alert
            variant={`info`}
            className={composeClassNames([sAlert])}
            style={gutter(`margin`, `top`, `lg`)}
          >
            <Translate tKey={`It is no longer possible to buy the serie.`} />
          </Alert>
        )}
      </Col>
      {data.description?.text?.length > 0 && (
        <Col className={"serie-description"}>
          <Hr variant={`light`} />
          <ReadMore
            rowsVisible={constants.Text.descriptionRowsLong}
            rowsCount={
              data.description.text.length /
              constants.Text.descriptionCharsPerRow
            }
          >
            <RichText size={`xn`}>
              <ReactMarkdown>{data.description.text}</ReactMarkdown>
            </RichText>
          </ReadMore>
        </Col>
      )}
    </Row>
  ) : null;
}

export default SerieIntroduction;
